import { getActionDate, isActionLate } from "@root/utils/actions";
import { useI18nContext } from "@root/utils/organisation";
import { useState } from "react";
import type { TPaperCandidateChallengeAction } from "./types";

export enum EnumPaperCandidateChallengeActionStatusFilter {
  DONE = "done",
  TODO = "todo",
  LATE = "late",
  MISSED = "missed",
}

/**
 * Group actions by month and year.
 * "sv-SE" locale is used to have a sortable string (YYYY-MM).
 */
const groupByMonthAndYear = <Action extends TPaperCandidateChallengeAction>(
  actions: Action[],
  timeZone: string
): { key: string; actions: Action[] }[] => {
  const group: Record<string, Action[]> = {};
  for (const action of actions) {
    const date = getActionDate(action);
    if (!date) {
      continue;
    }
    const monthYearKey = date.toLocaleString("sv-SE", { year: "numeric", month: "numeric", timeZone });
    if (!group[monthYearKey]) {
      group[monthYearKey] = [];
    }
    group[monthYearKey].push(action);
  }
  const keys = Object.keys(group).sort((a, b) => a.localeCompare(b));
  return keys.map((key) => ({
    key: key,
    actions: group[key],
  }));
};

const filterActionsByStatus = <Action extends TPaperCandidateChallengeAction>(
  actions: Action[],
  statusFilter: EnumPaperCandidateChallengeActionStatusFilter | null,
  timeZone: string
): Action[] => {
  if (!statusFilter) {
    return actions;
  }
  return actions.filter((action) => {
    const actionLate = isActionLate(action, timeZone);
    switch (statusFilter) {
      case EnumPaperCandidateChallengeActionStatusFilter.DONE:
        return action.status === "done";
      case EnumPaperCandidateChallengeActionStatusFilter.TODO:
        return action.status === "todo" && !actionLate;
      case EnumPaperCandidateChallengeActionStatusFilter.LATE:
        return action.status === "todo" && actionLate;
      case EnumPaperCandidateChallengeActionStatusFilter.MISSED:
        return action.status === "missed";
    }
  });
};

export const usePaperCandidateChallengeActions = <Action extends TPaperCandidateChallengeAction>(actions: Action[]) => {
  const { timeZone } = useI18nContext();
  const [statusFilter, setStatusFilter] = useState<EnumPaperCandidateChallengeActionStatusFilter | null>(null);
  const filteredActions = filterActionsByStatus(actions, statusFilter, timeZone);
  const groupedActions = groupByMonthAndYear(filteredActions, timeZone);

  return { groupedActions, statusFilter, setStatusFilter };
};
